import { useEffect, useMemo, useRef, useState } from "react";

const POINT_SPACING_PX = 34;
const MIN_PLOT_WIDTH_PX = 560;
const CHART_HEIGHT_PX = 220;
const PLOT_PADDING = { top: 14, right: 20, bottom: 40, left: 14 };
const AUTO_FOLLOW_THRESHOLD_PX = 24;

function parseTimestamp(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

export function normalizeConcurrentSeries(data = []) {
  const uniqueByTimestamp = new Map();
  data.forEach((point) => {
    const timestamp = parseTimestamp(point?.timestamp);
    if (!timestamp) return;
    const viewers = Number.isFinite(point?.viewers) ? Math.max(0, Number(point.viewers)) : 0;
    const normalized = { timestamp: timestamp.toISOString(), viewers };
    if (Number.isFinite(point?.watching)) normalized.watching = Math.max(0, Number(point.watching));
    uniqueByTimestamp.set(normalized.timestamp, normalized);
  });

  return Array.from(uniqueByTimestamp.values()).sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

export function mergeConcurrentSeries(existing = [], incoming = []) {
  const merged = new Map(existing.map((point) => [point.timestamp, point]));
  incoming.forEach((point) => merged.set(point.timestamp, point));

  return Array.from(merged.values()).sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

function formatTickLabel(timestamp, firstTimestamp) {
  const value = parseTimestamp(timestamp);
  if (!value) return "";
  const first = parseTimestamp(firstTimestamp);

  const sameDay = first && value.toDateString() === first.toDateString();
  return sameDay
    ? value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : `${value.toLocaleDateString([], { month: "2-digit", day: "2-digit" })} ${value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function getTickIndices(length) {
  if (!length) return [];
  if (length <= 8) return Array.from({ length }, (_value, index) => index);

  const step = Math.ceil(length / 8);
  const indices = new Set([0, length - 1]);
  for (let index = 0; index < length; index += step) indices.add(index);
  return Array.from(indices).sort((a, b) => a - b);
}

export function buildConcurrentChartModel(series = []) {
  const normalized = normalizeConcurrentSeries(series);
  const yMax = Math.max(1, ...normalized.map((point) => Math.max(point.viewers, point.watching || 0)));
  const hasWatching = normalized.some((point) => Number.isFinite(point.watching));
  const innerWidth = Math.max(MIN_PLOT_WIDTH_PX - PLOT_PADDING.left - PLOT_PADDING.right, Math.max(1, normalized.length - 1) * POINT_SPACING_PX);
  const width = innerWidth + PLOT_PADDING.left + PLOT_PADDING.right;
  const innerHeight = CHART_HEIGHT_PX - PLOT_PADDING.top - PLOT_PADDING.bottom;

  const points = normalized.map((point, index) => {
    const x = normalized.length <= 1
      ? PLOT_PADDING.left + innerWidth / 2
      : PLOT_PADDING.left + (index / (normalized.length - 1)) * innerWidth;
    const y = PLOT_PADDING.top + (1 - point.viewers / yMax) * innerHeight;

    const watchingY = PLOT_PADDING.top + (1 - (point.watching || 0) / yMax) * innerHeight;

    return {
      ...point,
      x,
      y,
      watchingY,
    };
  });

  const path = points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  const watchingPath = hasWatching
    ? points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.watchingY.toFixed(1)}`).join(" ")
    : "";
  const firstTimestamp = points[0]?.timestamp || null;
  const tickIndices = getTickIndices(points.length);
  const ticks = tickIndices.map((index) => ({
    index,
    x: points[index]?.x || PLOT_PADDING.left,
    label: formatTickLabel(points[index]?.timestamp, firstTimestamp),
  }));

  return {
    points,
    path,
    watchingPath,
    ticks,
    yMax,
    width,
    height: CHART_HEIGHT_PX,
    innerBottom: CHART_HEIGHT_PX - PLOT_PADDING.bottom,
    innerTop: PLOT_PADDING.top,
    latest: points[points.length - 1] || null,
  };
}

function formatBucket(minutes) {
  if (!minutes || minutes === 1) return "per minute";
  if (minutes < 60) return `per ${minutes} minutes (peak minute shown)`;
  if (minutes < 1440) return `per ${minutes / 60} hour${minutes === 60 ? "" : "s"} (peak minute shown)`;
  return `per ${minutes / 1440} day${minutes === 1440 ? "" : "s"} (peak minute shown)`;
}

export default function ConcurrentChart({ data = [], peak, peakWatching, current, currentWatching, bucketMinutes }) {
  const [seriesHistory, setSeriesHistory] = useState(() => normalizeConcurrentSeries(data));
  const [isFollowingLive, setIsFollowingLive] = useState(true);
  const scrollerRef = useRef(null);

  const normalizedIncoming = useMemo(() => normalizeConcurrentSeries(data), [data]);

  const bucketRef = useRef(bucketMinutes);
  useEffect(() => {
    if (!normalizedIncoming.length) return;
    // Bucket size grows as a page ages (1m → 5m → 1h…); never merge points of different bucket sizes.
    const bucketChanged = bucketRef.current !== bucketMinutes;
    bucketRef.current = bucketMinutes;
    setSeriesHistory((existing) => (bucketChanged ? normalizedIncoming : mergeConcurrentSeries(existing, normalizedIncoming)));
  }, [normalizedIncoming, bucketMinutes]);

  const model = useMemo(() => buildConcurrentChartModel(seriesHistory), [seriesHistory]);

  useEffect(() => {
    if (!isFollowingLive || !scrollerRef.current || !model.points.length) return;
    const scroller = scrollerRef.current;
    scroller.scrollLeft = scroller.scrollWidth;
  }, [isFollowingLive, model.points.length]);

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const remaining = scroller.scrollWidth - (scroller.scrollLeft + scroller.clientWidth);
    setIsFollowingLive(remaining <= AUTO_FOLLOW_THRESHOLD_PX);
  };

  const jumpToLatest = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollLeft = scroller.scrollWidth;
    setIsFollowingLive(true);
  };

  return (
    <section className="w-full max-w-full overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">Concurrent viewers</h2>
          <p className="text-sm text-slate-500">
            Distinct viewers on the page {formatBucket(bucketMinutes)}
            {model.watchingPath && " — solid: on page (incl. password/countdown), dashed: watching"}.
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
          Peak {peak || 0}{Number.isFinite(peakWatching) && model.watchingPath ? ` · ${peakWatching} watching` : ""}
        </span>
      </div>

      {model.points.length ? (
        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              Now on page <span data-testid="current-viewers-count" className="ml-2 text-slate-900">{Number.isFinite(current) ? current : model.latest?.viewers || 0}</span>
              {Number.isFinite(currentWatching) && (
                <span className="ml-4">Watching <span data-testid="current-watching-count" className="ml-2 text-slate-900">{currentWatching}</span></span>
              )}
            </p>
            {!isFollowingLive && (
              <button type="button" className="secondary-button text-xs" onClick={jumpToLatest}>
                Jump to latest
              </button>
            )}
          </div>

          <div className="flex w-full max-w-full gap-3">
            <div className="flex w-12 flex-col justify-between py-1 text-right text-xs font-semibold text-slate-500">
              <span data-testid="concurrent-y-max">{model.yMax}</span>
              <span className="text-[11px] uppercase tracking-[0.12em] text-slate-400">viewers</span>
              <span data-testid="concurrent-y-min">0</span>
            </div>

            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-slate-100 bg-slate-50"
              aria-label="Concurrent viewers timeline"
              data-testid="concurrent-scroller"
              tabIndex={0}
            >
              <svg
                viewBox={`0 0 ${model.width} ${model.height}`}
                className="block h-64"
                style={{ width: `${model.width}px` }}
                role="img"
                aria-label="Concurrent viewers trend line"
              >
                <line x1={PLOT_PADDING.left} x2={model.width - PLOT_PADDING.right} y1={model.innerTop} y2={model.innerTop} stroke="#dbe3ec" strokeWidth="1" strokeDasharray="4 4" />
                <line x1={PLOT_PADDING.left} x2={model.width - PLOT_PADDING.right} y1={(model.innerTop + model.innerBottom) / 2} y2={(model.innerTop + model.innerBottom) / 2} stroke="#e6ecf3" strokeWidth="1" strokeDasharray="2 6" />
                <line x1={PLOT_PADDING.left} x2={model.width - PLOT_PADDING.right} y1={model.innerBottom} y2={model.innerBottom} stroke="#dbe3ec" strokeWidth="1" />

                <path
                  d={model.path}
                  fill="none"
                  stroke="#FF3A20"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-all duration-700 ease-out"
                  data-testid="concurrent-line-path"
                />
                {model.watchingPath && (
                  <path
                    d={model.watchingPath}
                    fill="none"
                    stroke="#0f172a"
                    strokeWidth="2"
                    strokeDasharray="6 4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    data-testid="concurrent-watching-path"
                  />
                )}

                {model.latest && (
                  <g
                    style={{
                      transform: `translate(${model.latest.x}px, ${model.latest.y}px)`,
                      transformOrigin: "center",
                      transition: "transform 650ms ease-out",
                    }}
                  >
                    <circle r="9" fill="rgba(255, 58, 32, 0.14)" />
                    <circle r="5" fill="#FF3A20" />
                    <text x="10" y="-10" fontSize="10" fontWeight="700" fill="#9f1f10">{model.latest.viewers}</text>
                  </g>
                )}

                {model.ticks.map((tick) => (
                  <g key={`${tick.index}-${tick.label}`} transform={`translate(${tick.x}, ${model.height - 16})`}>
                    <line y1="-18" y2="-12" stroke="#c7d3e0" strokeWidth="1" />
                    <text textAnchor="middle" fontSize="10" fill="#627081">{tick.label}</text>
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>
      ) : (
        <p className="mt-5 text-sm text-slate-500">No concurrent viewer data yet.</p>
      )}
    </section>
  );
}
